/* eslint-disable react-hooks/exhaustive-deps */
import "./matching-detail.scss";
import { Flex, Form, Layout, Button } from "antd";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMessageApi } from "../../context/MessageContext";
import BackButton from "../../components/back-button";
import MatchingEspForm from "./components/matching-esp";
import { WorkflowServices } from "../../services/workflow";
import {
  IApWorkflowCommentHistory,
  IApWorkflowCommentHistoryResponse,
  IBookingSupplierCostMc,
  IDataEspWorkflowDetail,
  IEspWorkflowDetailResponse,
  IWorkflowType,
} from "../../interfaces/workflow";
import { IEspTable } from "../../interfaces/expect-supplier-payment";
import dayjs from "dayjs";
import { DATE_FORMAT_API } from "../../constants/common";
import { ExpectingSupplierPaymentServices } from "../../services/expect-supplier-payment";
import Loading from "../../components/loading";
import { useHasPermission } from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permission";
import { isEditEspWorkflowByStatus } from "../../utils/helper";
import TourEventOtherMatchingSetForm from "./components/tour-event-other-matching-set";
import { WorkflowTypeEnum } from "../../constants/enum";

const { Footer, Content } = Layout;

const formatDateDataBsc = (bscDetail: IBookingSupplierCostMc) => {
  if (bscDetail?.expectedSupplierPayments.length > 0) {
    const converted = (bscDetail?.expectedSupplierPayments).map((item: any) => ({
      ...item,
      dueDate: item.dueDate ? dayjs(item.dueDate) : null,
      supplierCode: bscDetail.supplierCode,
      leadPax: bscDetail.leadPaxName,
      bookingNo: bscDetail.bookingRef,
    }));
    return converted;
  }
  return [];
};

const TourEventOtherWorkflowDetailPage: React.FC<IWorkflowType> = ({ type }) => {
  const navigate = useNavigate();
  const messageApi = useMessageApi();
  const hasEditor = useHasPermission([PERMISSIONS.WORKFLOW_EDITOR]);
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [matchingDetail, setMatchingDetail] = useState<IDataEspWorkflowDetail>();
  const [isEspDisabled, setIsEspDisabled] = useState(true);
  const [isMatchingDisabled, setIsMatchingDisabled] = useState(true);
  const [comments, setComments] = useState<IApWorkflowCommentHistory[]>([]);

  const [matchingForm] = Form.useForm();
  const [espForm] = Form.useForm();

  const expectedSupplierPayments = Form.useWatch("expectedSupplierPayments", espForm) || [];
  const expectedSupplierPaymentId = matchingDetail?.expectedSupplierPaymentId!;

  const espList = expectedSupplierPayments.filter((item: IEspTable) => item.id === expectedSupplierPaymentId);

  const setEspForm = (bscDetail: IBookingSupplierCostMc) => {
    espForm.setFieldsValue({
      ...bscDetail,
      expectedSupplierPayments: formatDateDataBsc(bscDetail),
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resDetail, resComments]: [IEspWorkflowDetailResponse, IApWorkflowCommentHistoryResponse] =
        await Promise.all([WorkflowServices.getEspWorkflowDetail(id!), WorkflowServices.getEspWorkflowComments(id!)]);
      const { data, success } = resDetail;
      if (success) {
        setMatchingDetail(data);
        setEspForm(data.bookingSupplierCost);
        matchingForm.setFieldsValue({
          expectedSupplierPaymentId: data.expectedSupplierPaymentId,
          flag: data.flag,
          status: data.status,
          team: data.team,
          credit: data.credit,
          debit: data.debit,
          underOver: data.underOver,
        });
      }
      if (resComments.success) {
        const filterComments = (resComments?.data || []).filter((c) => c.comment);
        setComments(filterComments);
      }
    } catch (error: any) {
      messageApi.error(error?.message || "Call Api Failed.");
      navigate("/404");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    const checkEspValid = async () => {
      try {
        await validateEsp();
        setIsEspDisabled(espList.length > 0 ? false : true);
      } catch {
        setIsEspDisabled(true);
      }
    };
    setTimeout(() => {
      checkEspValid();
    }, 0);
  }, [Form.useWatch([], espForm), espList]);

  useEffect(() => {
    const checkMatchingValid = async () => {
      try {
        await matchingForm.validateFields();
        setIsMatchingDisabled(false);
      } catch {
        setIsMatchingDisabled(true);
      }
    };
    checkMatchingValid();
  }, [Form.useWatch([], matchingForm)]);

  const handleSaveEsp = async (isSkipMatching = true) => {
    if (isEspDisabled) return;
    try {
      const values = await validateEsp();

      if (isSkipMatching) {
        setLoading(true);
      }

      const body = values.expectedSupplierPayments.map((esp: IEspTable) => ({
        ...esp,
        dueDate: dayjs(esp.dueDate).format(DATE_FORMAT_API),
      }));
      await ExpectingSupplierPaymentServices.updateDetailBookingSupplierCost(
        values?.bookingRef,
        values?.supplierCode,
        body,
        Number(id)
      );
      if (isSkipMatching) {
        messageApi.success(`Update Expected Supplier Payment success!`);
      }
    } catch (err: any) {
      const message = err?.message;
      if (message) {
        messageApi.error(message);
      }
    } finally {
      if (isSkipMatching) {
        setLoading(false);
      }
    }
  };

  const validateEsp = async () => {
    const values = await espForm.validateFields();

    const totalEspCost = (values.expectedSupplierPayments || [])
      .filter((r: IEspTable) => r.isActive)
      .reduce((sum: number, item: any) => sum + (item.localCurrencyCost || 0), 0);

    const bscCost = values?.localCurrencyCost || 0;

    if (totalEspCost !== bscCost) {
      throw new Error("");
    }

    return values;
  };

  const handleSaveMatching = async (isConfirmMatch = false) => {
    if (isMatchingDisabled || isEspDisabled) return;
    try {
      setLoading(!isConfirmMatch);
      const values = await matchingForm.validateFields();
      const bookingSupplierCost = espForm.getFieldsValue();
      const body = {
        ...values,
        expectedSupplierPaymentId: matchingDetail?.expectedSupplierPaymentId!,
        bookingSupplierCost: {
          ...bookingSupplierCost,
          espTables: bookingSupplierCost.expectedSupplierPayments.map((esp: IEspTable) => ({
            ...esp,
            dueDate: dayjs(esp.dueDate).format(DATE_FORMAT_API),
          })),
        },
      };
      
      await WorkflowServices.updateEspWorkflowDetail(id!, body);
      messageApi.success(`Update Matching detail success!`);
      handleCancel();
    } catch (error) {
      messageApi.error(`Update Matching detail failed!`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(type === WorkflowTypeEnum.TourEvent ? "/tour-event-workflow" : "/other-workflow");
  };

  const renderCancelBtn = () => (
    <Button style={{ marginLeft: 8 }} color="primary" variant="outlined" onClick={handleCancel}>
      Cancel
    </Button>
  );

  return (
    <>
      {loading && <Loading />}
      {matchingDetail && (
        <div className={`matching-detail-page ${hasEditor ? "" : "fm-view-page"}`}>
          <Flex align="center" gap={16}>
            <BackButton
              label="Review list"
              to={type === WorkflowTypeEnum.TourEvent ? "/tour-event-workflow" : "/other-workflow"}
            />
            <h2 className="title-page">Matching details</h2>
          </Flex>
          <div className="wrap-matching">
            <Layout className="box-matching" style={{ minWidth: 820 }}>
              <Content className="content-matching">
                <TourEventOtherMatchingSetForm
                  form={matchingForm}
                  comments={comments}
                  matchingDetail={matchingDetail}
                />
              </Content>
              <Footer className="btn-action-matching">
                <Flex justify="end" gap={16}>
                  {renderCancelBtn()}
                  {hasEditor && (
                    <>
                      <Button
                        type="primary"
                        onClick={() => handleSaveMatching()}
                        disabled={isMatchingDisabled || isEspDisabled}
                      >
                        Save
                      </Button>
                    </>
                  )}
                </Flex>
              </Footer>
            </Layout>
            <Layout className="box-matching" style={{ minWidth: 940 }}>
              <Content className="content-matching">
                <MatchingEspForm
                  form={espForm}
                  espId={expectedSupplierPaymentId}
                  isDisabledByStatus={isEditEspWorkflowByStatus(matchingDetail.status)}
                  isTypeFlight={false}
                  isViewPg={false}
                />
              </Content>
              <Footer className="btn-action-matching">
                <Flex justify="end" gap={16}>
                  {renderCancelBtn()}
                  {hasEditor && (
                    <Button type="primary" onClick={() => handleSaveEsp()} disabled={isEspDisabled}>
                      Save
                    </Button>
                  )}
                </Flex>
              </Footer>
            </Layout>
          </div>
        </div>
      )}
    </>
  );
};

export default TourEventOtherWorkflowDetailPage;
